import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import {
  S_DISTANCE,
  S_POWER,
  S_HR,
  S_CADENCE,
  niceStep,
  buildPoints,
  buildSegments,
  averageValue,
  valueAtX,
  smoothProfile
} from '../utils/telemetry';
import './RiderCompareModal.css';

// Сравнение показателей двух участников на одном этапе.
// Данные берем из того же источника, что и визуальный реплей:
// GET /api/events/:event_id/results/replay/:route_id — профиль высот и телеметрия.
const METRICS = [
  { key: 'power', index: S_POWER, labelKey: 'results.replay_power', axisKey: 'results.compare_axis_power', fromZero: true },
  { key: 'heart_rate', index: S_HR, labelKey: 'results.replay_heart_rate', axisKey: 'results.compare_axis_heart_rate', fromZero: false },
  { key: 'cadence', index: S_CADENCE, labelKey: 'results.compare_metric_cadence', axisKey: 'results.compare_axis_cadence', fromZero: true }
];

const PRIMARY_COLOR = '#667eea';
const OPPONENT_COLOR = '#e0625a';

const formatFinishTime = (seconds) => {
  if (seconds === null || seconds === undefined) return null;

  const total = Math.round(parseFloat(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const RiderCompareModal = ({ isOpen, onClose, eventId, routeId, routeName, riderId, currentUserId }) => {
  const { t, language } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [metricKey, setMetricKey] = useState('power');
  // Усреднение значений: сглаженная линия вместо сырой телеметрии
  const [smoothed, setSmoothed] = useState(false);
  // Кто с кем сравнивается: оба участника переключаются селектами
  const [primaryId, setPrimaryId] = useState(null);
  const [opponentId, setOpponentId] = useState(null);
  // Дистанция, на которую наведен курсор (метры)
  const [cursorDistance, setCursorDistance] = useState(null);
  // Выделенный участок маршрута (метры) — график масштабируется по нему
  const [range, setRange] = useState(null);
  // Текущее выделение мышью/пальцем, пока кнопка не отпущена
  const [drag, setDrag] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  const svgRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Загрузка телеметрии этапа
  useEffect(() => {
    if (!isOpen || !eventId || !routeId) return undefined;

    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setMetricKey('power');
      setSmoothed(false);
      setPrimaryId(null);
      setOpponentId(null);
      setCursorDistance(null);
      setRange(null);
      setDrag(null);

      try {
        const response = await axios.get(`/api/events/${eventId}/results/replay/${routeId}`);
        if (!cancelled) setData(response.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || t('results.replay_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      setLoading(false);
    };
    // t намеренно не в зависимостях: функция перевода пересоздается на каждом
    // рендере провайдера и сбрасывала бы уже загруженные данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, routeId, reloadToken]);

  const riders = useMemo(() => (data && data.riders) || [], [data]);

  const riderName = (rider) => (
    language !== 'ru' && rider.first_name_eng && rider.last_name_eng
      ? `${rider.first_name_eng} ${rider.last_name_eng}`
      : `${rider.first_name} ${rider.last_name}`
  );

  // Пара по умолчанию: первый — текущий пользователь, если он проехал этап,
  // второй — тот, у чьей строки нажали иконку
  useEffect(() => {
    if (riders.length === 0) return;

    const byCurrentUser = riders.find(rider => rider.user_id === currentUserId);
    const clicked = riders.find(rider => rider.user_id === riderId);
    const defaultPrimary = byCurrentUser || clicked || riders[0];

    setPrimaryId(prev => (
      riders.some(rider => rider.user_id === prev) ? prev : defaultPrimary.user_id
    ));

    setOpponentId(prev => {
      const primaryUserId = riders.some(rider => rider.user_id === primaryId) ? primaryId : defaultPrimary.user_id;

      if (prev !== null && prev !== primaryUserId && riders.some(rider => rider.user_id === prev)) {
        return prev;
      }

      const opponentByClick = clicked && clicked.user_id !== primaryUserId ? clicked : null;
      const fallback = riders.find(rider => rider.user_id !== primaryUserId);

      return (opponentByClick || fallback || {}).user_id ?? null;
    });
  }, [riders, currentUserId, riderId, primaryId]);

  // Пока эффект не проставил выбор, показываем ту же пару по умолчанию —
  // иначе на кадр между загрузкой и эффектом мигало бы "нет телеметрии"
  const primary = useMemo(() => (
    riders.find(rider => rider.user_id === primaryId)
      || riders.find(rider => rider.user_id === currentUserId)
      || riders.find(rider => rider.user_id === riderId)
      || riders[0]
      || null
  ), [riders, primaryId, currentUserId, riderId]);

  const opponent = useMemo(
    () => riders.find(rider => rider.user_id === opponentId) || null,
    [riders, opponentId]
  );

  const metric = METRICS.find(item => item.key === metricKey) || METRICS[0];

  // Профиль сглаживаем скользящим средним — высота в FIT шумит на метровом уровне
  const profile = useMemo(() => smoothProfile((data && data.profile) || []), [data]);

  if (!isOpen) return null;

  // Геометрия графика
  const width = isMobile ? 380 : 720;
  const height = isMobile ? 320 : 380;
  const margin = isMobile
    ? { top: 18, right: 14, bottom: 46, left: 44 }
    : { top: 22, right: 24, bottom: 50, left: 58 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const shownRiders = [primary, opponent].filter(Boolean);
  // Значения, по которым рисуем: сырые или усредненные — от галочки
  const riderPoints = shownRiders.map(rider => buildPoints(rider.samples, metric.index, smoothed));
  const totalDistance = Math.max(
    (data && data.total_distance) || 0,
    profile.length ? profile[profile.length - 1][0] : 0,
    ...shownRiders.map(rider => (rider.samples.length ? rider.samples[rider.samples.length - 1][S_DISTANCE] : 0))
  );

  // Видимый участок: весь маршрут или выделенный отрезок
  const viewFrom = range ? range.from : 0;
  const viewTo = range ? range.to : totalDistance;
  const viewSpan = Math.max(1, viewTo - viewFrom);

  const xFor = (distance) => margin.left + (plotWidth * (distance - viewFrom)) / viewSpan;

  const inView = (distance) => distance >= viewFrom && distance <= viewTo;

  // Диапазон значений по обоим участникам в пределах видимого участка,
  // чтобы при увеличении отрезка линии разворачивались во всю высоту
  const metricValues = riderPoints.flatMap(points => (
    points
      .filter(([distance, value]) => value !== null && inView(distance))
      .map(([, value]) => value)
  ));
  const rawMin = metricValues.length ? Math.min(...metricValues) : 0;
  const rawMax = metricValues.length ? Math.max(...metricValues) : 1;
  const valueSpan = Math.max(1, rawMax - rawMin);
  const yMax = rawMax + valueSpan * 0.1;
  const yMin = metric.fromZero ? 0 : Math.max(0, rawMin - valueSpan * 0.1);

  const yFor = (value) => (
    margin.top + plotHeight - (plotHeight * (value - yMin)) / (yMax - yMin)
  );

  // Профиль высот — подложка в нижней части поля графика.
  // Высоты берем по видимому участку: на выделенном отрезке рельеф читается детальнее
  const profileHeight = plotHeight * 0.55;
  const visibleProfile = profile.filter(point => inView(point[0]));
  const altitudes = (visibleProfile.length > 1 ? visibleProfile : profile).map(point => point[1]);
  const minAltitude = altitudes.length ? Math.min(...altitudes) : 0;
  const maxAltitude = altitudes.length ? Math.max(...altitudes) : 1;
  const altitudeSpan = Math.max(1, maxAltitude - minAltitude);
  const yForAltitude = (altitude) => (
    margin.top + plotHeight - (profileHeight * (altitude - minAltitude)) / altitudeSpan
  );

  const profilePath = profile.length > 1
    ? `${profile.map(([distance, altitude], index) => (
      `${index === 0 ? 'M' : 'L'} ${xFor(distance)} ${yForAltitude(altitude)}`
    )).join(' ')} L ${xFor(profile[profile.length - 1][0])} ${margin.top + plotHeight} L ${xFor(profile[0][0])} ${margin.top + plotHeight} Z`
    : null;

  // Деления осей
  const yStep = niceStep((yMax - yMin) / 5);
  const yTicks = [];
  for (let value = Math.ceil(yMin / yStep) * yStep; value <= yMax; value += yStep) {
    yTicks.push(Math.round(value * 10) / 10);
  }

  const xStepKm = niceStep((viewSpan / 1000) / (isMobile ? 4 : 7));
  const xTicks = [];
  for (
    let km = Math.ceil((viewFrom / 1000) / xStepKm) * xStepKm;
    km * 1000 <= viewTo;
    km += xStepKm
  ) {
    xTicks.push(Math.round(km * 100) / 100);
  }

  const series = shownRiders.map((rider, index) => ({
    rider,
    color: index === 0 ? PRIMARY_COLOR : OPPONENT_COLOR,
    points: riderPoints[index],
    average: averageValue(riderPoints[index]),
    segments: buildSegments(riderPoints[index])
  }));

  const pathFor = (segment) => segment
    .map(([distance, value], index) => `${index === 0 ? 'M' : 'L'} ${xFor(distance)} ${yFor(value)}`)
    .join(' ');

  // Дистанция под указателем: работает и мышью, и пальцем.
  // За краями поля прижимаем к границе видимого участка — иначе выделение
  // обрывается, стоит увести курсор с графика
  const distanceAt = (clientX) => {
    const svg = svgRef.current;
    if (!svg || totalDistance <= 0) return null;

    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return null;

    const x = ((clientX - rect.left) / rect.width) * width;
    const ratio = Math.min(1, Math.max(0, (x - margin.left) / plotWidth));

    return viewFrom + ratio * viewSpan;
  };

  const handlePointerMove = (clientX) => {
    const distance = distanceAt(clientX);
    if (distance === null) return;

    setCursorDistance(distance);
    if (drag) setDrag(prev => ({ ...prev, to: distance }));
  };

  const startDrag = (clientX) => {
    const distance = distanceAt(clientX);
    if (distance === null) return;

    setCursorDistance(distance);
    setDrag({ from: distance, to: distance });
  };

  // Минимальная ширина выделения — иначе обычный клик схлопнул бы график в точку
  const MIN_RANGE_METERS = Math.max(50, viewSpan * 0.02);

  const finishDrag = () => {
    if (!drag) return;

    const from = Math.min(drag.from, drag.to);
    const to = Math.max(drag.from, drag.to);

    setDrag(null);
    if (to - from >= MIN_RANGE_METERS) setRange({ from, to });
  };

  const cursorX = cursorDistance !== null ? xFor(cursorDistance) : null;
  const dragFrom = drag ? Math.min(drag.from, drag.to) : null;
  const dragTo = drag ? Math.max(drag.from, drag.to) : null;

  // Подсказка и значения под курсором лежат в одной ячейке грида и оба всегда
  // отрисованы: скрытый вариант продолжает занимать место. Иначе смена подсказки
  // на значения меняла бы высоту окна, а вместе с ней прыгал бы и график
  const renderReadout = () => {
    const showValues = cursorDistance !== null;

    return (
      <div className="rider-compare-footnote">
        <div className={`rider-compare-readout-hint${showValues ? ' rider-compare-footnote-hidden' : ''}`}>
          {t('results.compare_hint')}
        </div>
        <div
          className={`rider-compare-readout${showValues ? '' : ' rider-compare-footnote-hidden'}`}
          aria-hidden={!showValues}
        >
          <span className="rider-compare-readout-distance">
            {((showValues ? cursorDistance : 0) / 1000).toFixed(1)} {t('results.compare_km')}
          </span>
          {series.map(({ rider, color, points }) => {
            const value = showValues ? valueAtX(points, cursorDistance) : null;

            return (
              <span key={`readout-${rider.user_id}`} className="rider-compare-readout-value">
                <span className="rider-compare-dot" style={{ background: color }} />
                {riderName(rider)}: <strong>{value === null ? '—' : `${value} ${t(metric.axisKey + '_unit')}`}</strong>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChart = () => (
    <>
      <svg
        ref={svgRef}
        className="rider-compare-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={t('results.compare_title')}
        onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX); }}
        onMouseMove={(e) => handlePointerMove(e.clientX)}
        onMouseUp={finishDrag}
        onMouseLeave={() => { finishDrag(); setCursorDistance(null); }}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
        onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
        onTouchEnd={finishDrag}
      >
        {/* Все, что рисуется по дистанции, обрезаем полем графика:
            при выделенном участке точки за его границами уходят за оси */}
        <clipPath id="rider-compare-plot-clip">
          <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} />
        </clipPath>

        {/* Рельеф этапа — подложка под линиями показателей */}
        {profilePath && (
          <path className="rider-compare-profile" d={profilePath} clipPath="url(#rider-compare-plot-clip)" />
        )}

        {/* Сетка и подписи оси Y (выбранный показатель) */}
        {yTicks.map(tick => (
          <g key={`y-${tick}`}>
            <line
              className="rider-compare-grid"
              x1={margin.left}
              x2={width - margin.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text
              className="rider-compare-axis-label"
              x={margin.left - 8}
              y={yFor(tick) + 4}
              textAnchor="end"
            >
              {tick}
            </text>
          </g>
        ))}

        {/* Подписи оси X (дистанция) */}
        {xTicks.map(km => (
          <g key={`x-${km}`}>
            <line
              className="rider-compare-grid rider-compare-grid-vertical"
              x1={xFor(km * 1000)}
              x2={xFor(km * 1000)}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
            <text
              className="rider-compare-axis-label"
              x={xFor(km * 1000)}
              y={height - margin.bottom + 18}
              textAnchor="middle"
            >
              {km}
            </text>
          </g>
        ))}

        <text
          className="rider-compare-axis-title"
          x={margin.left + plotWidth / 2}
          y={height - 10}
          textAnchor="middle"
        >
          {t('results.compare_axis_distance')}
        </text>
        <text
          className="rider-compare-axis-title"
          x={-(margin.top + plotHeight / 2)}
          y={14}
          transform="rotate(-90)"
          textAnchor="middle"
        >
          {t(metric.axisKey)}
        </text>

        {/* Линии участников */}
        {series.map(({ rider, color, segments }) => (
          <g key={`line-${rider.user_id}`} clipPath="url(#rider-compare-plot-clip)">
            {segments.map((segment, index) => (
              <path
                key={`segment-${rider.user_id}-${index}`}
                className="rider-compare-line"
                d={pathFor(segment)}
                stroke={color}
              />
            ))}
          </g>
        ))}

        {/* Выделяемый участок */}
        {drag && dragTo > dragFrom && (
          <rect
            className="rider-compare-selection"
            x={xFor(dragFrom)}
            y={margin.top}
            width={Math.max(0, xFor(dragTo) - xFor(dragFrom))}
            height={plotHeight}
          />
        )}

        {/* Курсор по дистанции */}
        {cursorX !== null && (
          <g clipPath="url(#rider-compare-plot-clip)">
            <line
              className="rider-compare-cursor"
              x1={cursorX}
              x2={cursorX}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
            {series.map(({ rider, color, points }) => {
              const value = valueAtX(points, cursorDistance);
              if (value === null) return null;

              return (
                <circle
                  key={`cursor-${rider.user_id}`}
                  className="rider-compare-cursor-point"
                  cx={cursorX}
                  cy={yFor(value)}
                  r={isMobile ? 5 : 4.5}
                  fill={color}
                />
              );
            })}
          </g>
        )}
      </svg>

      {renderReadout()}
    </>
  );

  const renderLegend = () => (
    <div className="rider-compare-legend">
      {series.map(({ rider, color, average }) => (
        <div key={`legend-${rider.user_id}`} className="rider-compare-legend-item">
          <span className="rider-compare-dot" style={{ background: color }} />
          <div>
            <div className="rider-compare-legend-name">
              {riderName(rider)}
              {rider.user_id === currentUserId && (
                <span className="rider-compare-you">{t('results.compare_you')}</span>
              )}
            </div>
            <div className="rider-compare-legend-stats">
              {t('results.compare_average')}: <strong>{average === null ? '—' : `${average} ${t(metric.axisKey + '_unit')}`}</strong>
              {formatFinishTime(rider.finish_time) && ` · ${formatFinishTime(rider.finish_time)}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (loading) return <div className="rider-compare-state">{t('common.loading')}</div>;

    if (error) {
      return (
        <div className="rider-compare-state">
          <div>{error}</div>
          <button className="rider-compare-retry" onClick={() => setReloadToken(prev => prev + 1)}>
            {t('results.replay_retry')}
          </button>
        </div>
      );
    }

    if (!primary) return <div className="rider-compare-state">{t('results.compare_no_data')}</div>;

    return (
      <>
        <div className="rider-compare-controls">
          <div className="rider-compare-metrics">
            {METRICS.map(item => (
              <button
                key={item.key}
                type="button"
                className={`rider-compare-metric ${item.key === metric.key ? 'rider-compare-metric-active' : ''}`}
                onClick={() => setMetricKey(item.key)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>

          <label className="rider-compare-smooth">
            <input
              type="checkbox"
              checked={smoothed}
              onChange={(e) => setSmoothed(e.target.checked)}
            />
            <span>{t('results.compare_smooth')}</span>
          </label>

          <div className="rider-compare-riders">
            <label className="rider-compare-select">
              <span>{t('results.compare_who')}</span>
              <select
                value={primary.user_id}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  // Выбрали того, с кем сравнивались — просто меняем участников местами
                  if (next === opponentId) setOpponentId(primary.user_id);
                  setPrimaryId(next);
                }}
              >
                {riders.map(rider => (
                  <option key={rider.user_id} value={rider.user_id}>
                    {rider.position ? `${rider.position}. ` : ''}{riderName(rider)}
                  </option>
                ))}
              </select>
            </label>

            <label className="rider-compare-select">
              <span>{t('results.compare_with')}</span>
              <select
                value={opponentId === null ? '' : opponentId}
                onChange={(e) => setOpponentId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{t('results.compare_nobody')}</option>
                {riders
                  .filter(rider => rider.user_id !== primary.user_id)
                  .map(rider => (
                    <option key={rider.user_id} value={rider.user_id}>
                      {rider.position ? `${rider.position}. ` : ''}{riderName(rider)}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        </div>

        {range && (
          <div className="rider-compare-range">
            <span>
              {t('results.compare_range')
                .replace('{from}', (range.from / 1000).toFixed(1))
                .replace('{to}', (range.to / 1000).toFixed(1))}
            </span>
            <button type="button" className="rider-compare-range-reset" onClick={() => setRange(null)}>
              {t('results.replay_zoom_reset')}
            </button>
          </div>
        )}

        {renderLegend()}
        {renderChart()}
      </>
    );
  };

  return (
    // Окно открывается поверх модалки результатов — клик по подложке
    // не должен закрывать ее вместе с графиком
    <div
      className="rider-compare-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="rider-compare-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rider-compare-header">
          <div>
            <h3 className="rider-compare-title">{t('results.compare_title')}</h3>
            <div className="rider-compare-subtitle">{routeName}</div>
          </div>
          <button className="rider-compare-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="rider-compare-body">{renderBody()}</div>
      </div>
    </div>
  );
};

export default RiderCompareModal;
